import type { ReactNode } from 'react';
import { CallPad } from './CallPad';
import { YesNoPad } from './YesNoPad';
import { WhyNotPad } from './WhyNotPad';
import { StepBackButton } from './StepBackButton';
import type { AnswerFlow } from '../../hooks/useAnswerFlow';
import type { LegalBids } from '../../types/problems';

/**
 * The right panel's contents: the answer chain while the question is open, and the caller's
 * verdict once it is graded.
 *
 * The INPUT is shared — a Yes/No drill runs Yes/No → "Why not?" → "Which bid instead?", a call
 * module shows the pad, and both carry the same secondary action beneath — so it lives here with
 * <see cref="AnswerFlow"/> driving it. The VERDICT is not: what a session says after an answer is
 * its own voice (Practice names the call and offers the next hand; a round that withholds
 * commentary until the end would say something quite different), so it arrives as a slot.
 */
export function AnswerPanel({
  isYesNo,
  prompt,
  flow,
  legalBids,
  busy,
  footer,
  verdict,
}: {
  /** Yes/No drill (opening-bid modules) rather than a plain call pad. */
  isYesNo: boolean;
  /** The Yes/No question itself. Ignored for call pads, which ask a fixed question. */
  prompt?: ReactNode;
  flow: AnswerFlow;
  legalBids: LegalBids;
  /** True while something is in flight — freezes the pads without unmounting them. */
  busy: boolean;
  /**
   * The secondary action under the pad (Practice's "Skip Hand"). Rendered outside the step
   * blocks, so it stays put down the whole No path — see the note at its call site below.
   */
  footer?: ReactNode;
  /** The graded state. When given, it REPLACES the pads: the question is answered. */
  verdict?: ReactNode;
}) {
  if (verdict) {
    return <div className="space-y-3">{verdict}</div>;
  }

  // After "No": first pick a reason (WhyNotPad), and if it is "should open something else", then
  // name the bid that should have been opened (the call pad). Neither has been graded yet.
  //
  // The whole No follow-up chain is `inNoPath`. On padswap viewports (single-column stack, short
  // screen — tailwind.config.js) the chain REPLACES the Yes/No pad instead of growing under it:
  // stacked, the three steps together are what pushed the panel past the viewport and put a
  // scrollbar on a page whose contract is app-like no-scroll. Short desktops (`short`, up to 750
  // tall) swap too: at 1093x614 and 1180x746 the stacked bid step pushed the status line under
  // the frame's bottom edge. So does every phone (`narrow`): at 390x844, a North/South hand with
  // its board stacked above it left the stacked steps no room and the answer panel ran past the
  // frame (spec 031, 2026-10-01). The desktop arm reaches 800 tall rather than `short`'s 750
  // since the bid pad's level chips wrap to a second row wherever they would be under 38px
  // (client, 2026-10-02): on the 768-tall laptops and landscape iPads (1366x768, 1024x768) the
  // stacked bid step then cut off the status line. Portrait tablets and taller desktops keep the
  // stacked presentation, where seeing the No above its follow-up is worth the height.
  const inNoPath = isYesNo && flow.chosen === 'no';
  // The "Why not?" pad stays mounted while the "insufficient points" grade is in flight (reason
  // 'pass', not yet answered) rather than unmounting on the click: it holds the chosen reason's
  // brass ring on screen until the verdict arrives — and on padswap viewports its unmount would
  // flash the Yes/No pad back for the round trip.
  const awaitingWhy = inNoPath && flow.reason !== 'other';
  const awaitingOtherBid = inNoPath && flow.reason === 'other';

  if (!isYesNo) {
    return (
      <div className="space-y-3">
        <CallPad
          onSubmit={flow.submitCall}
          legalBids={legalBids}
          onLevelChange={flow.setSelectedLevel}
          disabled={busy}
        />
        {footer}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* On padswap viewports the No path swaps this pad out for its follow-up step; the back
          arrow on each step is the way home. display:none (not unmount) so the chain keeps one
          shape everywhere and resizing mid-question just changes which steps are in view. */}
      <div className={inNoPath ? 'padswap:hidden narrow:hidden [@media(max-height:800px)]:hidden' : undefined}>
        <YesNoPad
          prompt={prompt}
          onAnswer={flow.answerYesNo}
          chosen={flow.chosen}
          // Frozen only while the "insufficient points" grade is in flight (it resolves to the
          // verdict and unmounts this pad). Otherwise Yes stays live so a mis-clicked No — or a
          // change of mind during the bid step — can still be corrected by choosing Yes where
          // this pad is visible.
          disabled={flow.reason === 'pass'}
        />
      </div>
      {awaitingWhy && (
        <WhyNotPad
          onReason={flow.chooseReason}
          chosen={flow.reason}
          // Frozen with the Yes/No pad while the "insufficient points" grade is in flight.
          disabled={flow.reason === 'pass'}
          onBack={flow.backToYesNo}
        />
      )}
      {awaitingOtherBid && (
        <div className="space-y-3 padswap:space-y-2" data-testid="other-bid-step">
          {/* The step's own prompt row, mirroring WhyNotPad's: the back arrow returns to the
              reason choice — the only way back to "Insufficient points" on ANY viewport once
              this step is reached. */}
          <div className="flex items-center gap-2">
            <StepBackButton
              onClick={flow.backToReason}
              disabled={busy}
              label="Back to Why not?"
              testId="other-bid-back"
            />
            <p className="text-[15px] font-semibold text-ink">Which bid instead?</p>
          </div>
          {/* Name the bid that should have been opened. The auction shown is only the calls
              before the decision (leading passes at most), so the legal calls are the opening
              bids — and ONLY the bids: Pass is the other reason button's territory
              ("Insufficient points — should pass"), so offering it here both duplicated that
              choice and set a grading trap (a Pass submitted as no:other:p is marked wrong even
              when "should pass" was the right answer). Dbl/Rdbl are never openings. Dropping the
              specials row also buys the height that let this step fit a 360x640 phone without a
              scrollbar. */}
          <CallPad
            onSubmit={flow.submitOtherBid}
            legalBids={{ ...legalBids, canPass: false, canDouble: false, canRedouble: false }}
            onLevelChange={flow.setSelectedLevel}
            disabled={busy}
          />
        </div>
      )}
      {/* Outside the step blocks, so the secondary action stays put down the whole No path. On
          padswap viewports that path REPLACES the Yes/No pad, so an action attached to the pad
          alone would vanish mid-question. Nothing is graded until submit, so leaving from any
          step is safe. */}
      {footer}
    </div>
  );
}
